import React from "react";

import { DashboardStatusTone } from "./DashboardStatusBadge";

const BANNER_TONE: Record<DashboardStatusTone, string> = {
  neutral: "border-slate-200 bg-slate-50",
  info: "border-blue-200 bg-blue-50",
  warning: "border-amber-200 bg-amber-50",
  error: "border-rose-200 bg-rose-50",
};

const HEADING_TONE: Record<DashboardStatusTone, string> = {
  neutral: "text-slate-900",
  info: "text-blue-900",
  warning: "text-amber-900",
  error: "text-rose-800",
};

const BODY_TONE: Record<DashboardStatusTone, string> = {
  neutral: "text-slate-600",
  info: "text-blue-800",
  warning: "text-amber-800",
  error: "text-rose-700",
};

export interface IDashboardStatusBannerCta {
  label: string;
  onClick: (event: React.MouseEvent) => void;
  /** "solid" = filled orange button (empty states); "link" = inline underline (partial notices). */
  variant?: "solid" | "link";
}

interface IProps {
  tone?: DashboardStatusTone;
  heading?: string;
  body?: string;
  /** Rendered as a bulleted list under the body (e.g. metadata warnings). */
  items?: string[];
  cta?: IDashboardStatusBannerCta;
  align?: "left" | "center";
  /** "lg" uses roomier padding + a larger heading, for full-panel empty states. */
  size?: "sm" | "lg";
  children?: React.ReactNode;
}

/**
 * Shared status callout for the dashboard. Replaces the per-status inline slate/amber/rose
 * boxes that were duplicated across NextGenDashboard (empty / error / partial / notes /
 * freshness) so every status surface renders with one consistent treatment (AC #2).
 */
export const DashboardStatusBanner = (props: IProps) => {
  const tone = props.tone ?? "neutral";
  const align = props.align ?? "left";
  const size = props.size ?? "sm";

  const padding = size === "lg" ? "p-6" : "p-4";
  const alignment = align === "center" ? "text-center" : "";
  const headingSize = size === "lg" ? "text-lg" : "";

  const handleCta = (event: React.MouseEvent) => {
    event.stopPropagation();
    props.cta?.onClick(event);
  };

  return (
    <div className={`rounded-xl border ${BANNER_TONE[tone]} ${padding} ${alignment}`}>
      {props.heading ? (
        <div className={`font-semibold ${headingSize} ${HEADING_TONE[tone]}`}>
          {props.heading}
        </div>
      ) : null}
      {props.body ? (
        <p
          className={`text-sm ${BODY_TONE[tone]} ${props.heading ? "mt-1" : ""}`}
        >
          {props.body}
        </p>
      ) : null}
      {props.items && props.items.length > 0 ? (
        <ul className={`mt-2 list-disc pl-5 text-sm ${BODY_TONE[tone]}`}>
          {props.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {props.children}
      {props.cta ? (
        props.cta.variant === "link" ? (
          <button
            type="button"
            className={`mt-2 font-medium underline ${HEADING_TONE[tone]}`}
            onClick={handleCta}
          >
            {props.cta.label}
          </button>
        ) : (
          <button
            type="button"
            className="mt-4 cursor-pointer rounded bg-zeos-orange-dk px-3 py-1.5 text-sm text-white"
            onClick={handleCta}
          >
            {props.cta.label}
          </button>
        )
      ) : null}
    </div>
  );
};
